import React, { useState } from 'react';
import { Chart } from 'primereact/chart';
import { ReportHeader } from '../../header/report-header';
import {
  CourseFinancialBranchBreakdownFilter,
  CourseFinancialBranchBreakdownFilterDatamodel,
} from './side-bar-filter/course-financial-branch-breakdown-filter';
import Colors from '../../../../utils/colors';
import { observer } from 'mobx-react-lite';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import './course-financial-branch-breakdown.css';
import { getCourseFinancialBranchBreakdown } from './operations/get-course-financial-branch-breakdown';
import { toast } from 'react-toastify';
import { ReportProgram } from '../../../../api/types/reports/report-abstract-program.type';
import { ReportCourseType } from '../../../../api/types/reports/report-abstract-course.type';

export interface FinancialData extends ReportProgram, ReportCourseType {
  year: string;
  courseName: string;
  program: string;
  totalHours: number;
  hourlyRate: number;
  totalRevenue: number;
  status: string;
}

export const CourseFinancialBranchBreakdown = observer(() => {
  const [financialData, setFinancialData] = useState<FinancialData[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (
    filters: CourseFinancialBranchBreakdownFilterDatamodel,
  ) => {
    await getCourseFinancialBranchBreakdown(
      filters,
      setLoading,
      setFinancialData,
    );
  };

  // Prepare labels for each item
  const labels = financialData.map(
    (item) => `${item.courseName} (${item.programStartYear})`,
  );

  // Prepare data for the first chart - Hours by course
  const hoursChartData = {
    labels: labels,
    datasets: [
      {
        label: 'שעות לימוד',
        data: financialData.map((item) => Number(item.totalHours)),
        backgroundColor: '#2196F3', // Info blue color for hours
        borderWidth: 0,
      },
    ],
  };

  // Prepare data for the second chart - Course expenses
  const expensesChartData = {
    labels: labels,
    datasets: [
      {
        label: 'הוצאות קורס',
        data: financialData.map((item) => Number(item.totalRevenue)),
        backgroundColor: '#FF9999', // Light red color for expenses
        borderWidth: 0,
      },
    ],
  };

  // Prepare data for the third chart - Average cost per hour by course
  const avgCostChartData = {
    labels: labels,
    datasets: [
      {
        label: 'עלות ממוצעת לשעה',
        data: financialData.map((item) => {
          const hours = Number(item.totalHours);
          const revenue = Number(item.totalRevenue);
          return hours > 0 ? revenue / hours : 0;
        }),
        backgroundColor: '#FFA500', // Orange color for average cost
        borderWidth: 0,
      },
    ],
  };

  const commonChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
        },
      },
      x: {
        title: {
          display: true,
        },
        ticks: {
          maxRotation: 45,
          callback: function (value: string | number): string | string[] {
            const label = this.getLabelForValue(value);
            return label.length > 40 ? label.substring(0, 17) + '...' : label;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += context.parsed.y.toFixed(2);
            }
            return label;
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  const hoursChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: {
          display: true,
          text: 'מספר שעות',
        },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: {
          display: true,
          text: 'שם הקורס',
        },
      },
    },
  };

  const expensesChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: {
          display: true,
          text: 'הוצאות (₪)',
        },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: {
          display: true,
          text: 'שם הקורס',
        },
      },
    },
  };

  const avgCostChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: {
          display: true,
          text: 'עלות לשעה (₪)',
        },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: {
          display: true,
          text: 'שם הקורס',
        },
      },
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח פירוט פיננסי של קורסים לפי שלוחה"
        description="דו״ח זה מציג פירוט פיננסי מקיף של קורסים בשלוחה נבחרת לאורך שנים, כולל נתוני שעות לימוד, תעריפים והכנסות."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'ניתוח פיננסי של קורסים בשלוחה' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="financial"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <CourseFinancialBranchBreakdownFilter onSearch={handleSearch} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 230px)',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              שעות לימוד לפי קורס ושנה
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={hoursChartData}
                options={hoursChartOptions}
              />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              הוצאות קורס לפי שנה
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={expensesChartData}
                options={expensesChartOptions}
              />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              עלות ממוצעת לשעה לפי קורס ושנה
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={avgCostChartData}
                options={avgCostChartOptions}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
